import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiMessageCircle } from "react-icons/fi";
import { useSession } from "./session";
import { formatCount } from "./api";

// 7 cards are visible (offsets -3..3); the extra ones wrap around while hidden.
const COUNT = 9;
const VISIBLE = 3;
const AUTOPLAY_MS = 4500;
let cache = null;

// off -> [translateX in --step units, scale, rotateY deg, brightness]
const POSE = {
  0: [0, 1, 0, 1],
  1: [1, 0.9, -28, 0.84],
  2: [1.85, 0.8, -38, 0.7],
  3: [2.6, 0.7, -44, 0.56],
};

function pose(off) {
  const abs = Math.min(Math.abs(off), VISIBLE + 1);
  const sign = off < 0 ? -1 : 1;
  const [x, s, r, b] = POSE[abs] || [3.2, 0.6, -50, 0.4];
  return { x: x * sign, s, r: r * sign, b, abs };
}

const transformOf = (p) =>
  `translateX(calc(${p.x} * var(--step))) scale(${p.s}) rotateY(${p.r}deg)`;

export default function HeroCarousel() {
  const { api, auth } = useSession();
  const location = useLocation();
  const [items, setItems] = useState(cache || []);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);
  const drag = useRef(null);
  const moved = useRef(false);

  useEffect(() => {
    if (cache || auth.isLoading) return;
    const abort = new AbortController();
    api("/api/search?sort=popular&page=1", { signal: abort.signal })
      .then((r) => r.json())
      .then((d) => {
        cache = (d.results || []).filter((c) => c.card).slice(0, COUNT);
        setItems(cache);
      })
      .catch(() => {});
    return () => abort.abort();
  }, [api, auth.isLoading]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  const n = items.length;
  const go = useCallback(
    (delta) => n && setActive((a) => (a + delta + n) % n),
    [n],
  );

  useEffect(() => {
    if (n < 2 || paused || reduced) return;
    const t = setInterval(() => {
      if (!document.hidden) go(1);
    }, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [n, paused, reduced, go]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      go(1);
    }
  };

  const onPointerDown = (e) => {
    drag.current = e.clientX;
    moved.current = false;
  };
  const onPointerUp = (e) => {
    if (drag.current == null) return;
    const dx = e.clientX - drag.current;
    drag.current = null;
    if (Math.abs(dx) > 40) {
      moved.current = true;
      go(dx < 0 ? 1 : -1);
    }
  };

  if (!n) {
    return (
      <div className="hero-carousel is-loading" aria-hidden="true">
        <div className="coverflow-stage">
          {[-3, -2, -1, 0, 1, 2, 3].map((o) => {
            const p = pose(o);
            return (
              <div
                key={o}
                className="coverflow-card coverflow-skeleton"
                style={{
                  transform: transformOf(p),
                  zIndex: 10 - p.abs,
                }}
              />
            );
          })}
        </div>
      </div>
    );
  }

  const half = n >> 1;
  const current = items[active];

  return (
    <section
      className="hero-carousel"
      aria-roledescription="carousel"
      aria-label="Featured characters"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="hero-star s1" aria-hidden="true">✦</span>
      <span className="hero-star s2" aria-hidden="true">✦</span>
      <span className="hero-star s3" aria-hidden="true">✦</span>
      <span className="hero-star s4" aria-hidden="true">✦</span>
      <button
        type="button"
        className="coverflow-arrow prev"
        aria-label="Previous character"
        onClick={() => go(-1)}
      >
        <FiChevronLeft />
      </button>
      <div
        className="coverflow-stage"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => (drag.current = null)}
      >
        {items.map((c, i) => {
          const off = ((i - active + n + half) % n) - half;
          const p = pose(off);
          const hidden = p.abs > VISIBLE;
          const style = {
            transform: transformOf(p),
            zIndex: 10 - p.abs,
            filter: `brightness(${p.b})`,
            opacity: hidden ? 0 : 1,
            pointerEvents: hidden ? "none" : "auto",
          };
          const inner = (
            <>
              <img src={c.card} alt={c.name} draggable="false" />
              <span className="coverflow-info" aria-hidden={off !== 0}>
                <strong>{c.name}</strong>
                <small>
                  <FiMessageCircle aria-hidden="true" />
                  {formatCount(c.stats?.messages)}
                  <span className="sr-only"> messages</span>
                </small>
              </span>
            </>
          );
          const cls = `coverflow-card${off === 0 ? " is-center" : ""}`;
          // Same element type for every card so React never remounts it and
          // the transform transition always plays.
          return (
            <Link
              key={c.publicId}
              className={cls}
              style={style}
              to={`/character/${c.publicId}`}
              state={{ from: location.pathname + location.search }}
              tabIndex={hidden ? -1 : 0}
              aria-hidden={hidden || undefined}
              aria-label={off === 0 ? `View ${c.name}` : `Show ${c.name}`}
              draggable="false"
              onClick={(e) => {
                if (off === 0) {
                  if (moved.current) e.preventDefault();
                  return;
                }
                e.preventDefault();
                if (!moved.current) setActive(i);
              }}
            >
              {inner}
            </Link>
          );
        })}
      </div>
      <button
        type="button"
        className="coverflow-arrow next"
        aria-label="Next character"
        onClick={() => go(1)}
      >
        <FiChevronRight />
      </button>
      <span className="sr-only" aria-live="polite">
        {current?.name}
      </span>
    </section>
  );
}
